import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { Breadcrumb, Icon, Input, Label } from "../../ui";
import { useMetaAdsAdForEdit } from "../../../hooks/metaAds/useMetaAdsAdForEdit";
import {
  isMetaAdsApiConfigured,
  patchMetaAdsAd,
} from "../../../hooks/metaAds/useMetaAdsApi";
import { isMetaAdsExperienceAvailable } from "../../../hooks/metaAds/useMetaAdsMode";
import { cn } from "@/lib/utils";
import {
  metaAdsFormFieldLabelClass,
  metaAdsFormSectionClass,
  metaAdsFormSectionLabelClass,
  metaAdsGradientCtaClass,
  metaAdsOutlineCtaClass,
  metaAdsTextareaClass,
  metaAdsTitleGradientClass,
} from "./metaAdsTheme";
import MetaAdsCreativePreview from "./MetaAdsCreativePreview";
import MetaAdsBusinessPicker from "./MetaAdsBusinessPicker";
import {
  ensureMetaAdsDestinationUrlStoreUtm,
  metaAdsDestinationUrlHasCorrectStoreUtm,
  parseMetaAdsStoreIdFromDestinationUrl,
} from "./metaAdsUtils";

function maskNumericIdForDisplay(value) {
  if (!value) {
    return "";
  }
  return "•".repeat(String(value).length);
}

function SkeletonPulse({ className }) {
  return (
    <div
      className={cn("bg-muted/45 animate-pulse rounded-lg", className)}
      aria-hidden
    />
  );
}

/**
 * Mirrors edit-ad layout: form sections + preview column (desktop) / stacked preview (mobile).
 */
function MetaAdsEditAdPageSkeleton({ ariaLabel }) {
  return (
    <div
      className="grid min-h-0 gap-8 lg:grid-cols-2 lg:items-start"
      aria-busy="true"
      aria-label={ariaLabel}
      role="status"
    >
      <div className="space-y-6">
        <section className={metaAdsFormSectionClass}>
          <div className="mb-5 flex items-center gap-2">
            <SkeletonPulse className="h-5 w-5 shrink-0 rounded-full" />
            <SkeletonPulse className="h-5 w-44 sm:w-52" />
          </div>
          <SkeletonPulse className="mb-2 h-3.5 w-24" />
          <SkeletonPulse className="h-12 w-full rounded-xl" />
        </section>

        <section className={metaAdsFormSectionClass}>
          <SkeletonPulse className="mb-4 h-5 w-28" />
          <SkeletonPulse className="h-[52px] w-full rounded-xl" />
        </section>

        <section className={metaAdsFormSectionClass}>
          <SkeletonPulse className="mb-2 h-5 w-48 sm:w-56" />
          <SkeletonPulse className="mb-4 h-3.5 w-full max-w-md" />
          <SkeletonPulse className="mb-2 h-3.5 w-32" />
          <SkeletonPulse className="h-12 w-full rounded-xl" />
        </section>

        <section className={metaAdsFormSectionClass}>
          <SkeletonPulse className="mb-2 h-5 w-44 sm:w-52" />
          <SkeletonPulse className="mb-4 h-3.5 w-full max-w-lg" />
          <SkeletonPulse className="h-[72px] w-full rounded-xl" />
        </section>

        <section className={metaAdsFormSectionClass}>
          <SkeletonPulse className="mb-4 h-5 w-28" />
          <SkeletonPulse className="mb-2 h-3.5 w-36" />
          <SkeletonPulse className="mb-2 h-12 w-full rounded-xl" />
          <SkeletonPulse className="mb-6 h-3 w-full max-w-sm" />
          <SkeletonPulse className="mb-2 h-3.5 w-28" />
          <SkeletonPulse className="min-h-[104px] w-full rounded-xl" />
          <SkeletonPulse className="mt-2 h-3 w-full max-w-md" />
        </section>

        <div className="lg:hidden">
          <div className="border-border bg-card shadow-primary/5 rounded-2xl border p-4 shadow-lg backdrop-blur-sm">
            <SkeletonPulse className="mx-auto mb-3 h-3 w-36 rounded-md" />
            <div className="border-border overflow-hidden rounded-xl border">
              <div className="bg-muted/25 border-border h-9 border-b border-white/10" />
              <div className="bg-[#0a0a0a] p-2 sm:p-3">
                <div className="mx-auto w-full max-w-[390px]">
                  <div
                    className="mx-auto h-[min(280px,42vh)] w-full animate-pulse rounded-lg bg-zinc-800/50"
                    aria-hidden
                  />
                </div>
              </div>
              <div className="border-border border-t border-white/5 px-3 py-2">
                <SkeletonPulse className="mx-auto h-8 w-24 rounded-lg" />
              </div>
            </div>
          </div>
        </div>

        <SkeletonPulse className="h-14 w-full rounded-xl" />
      </div>

      <div className="border-border bg-card shadow-primary/5 hidden min-h-0 flex-col overflow-hidden rounded-2xl border shadow-lg backdrop-blur-sm lg:sticky lg:top-6 lg:z-10 lg:flex lg:h-[calc(100svh-1.75rem)] lg:max-h-[calc(100svh-1.75rem)] lg:self-start">
        <div className="from-brand-cyan/10 shrink-0 border-b border-white/10 bg-gradient-to-b to-transparent px-4 pt-3 pb-2">
          <SkeletonPulse className="mb-2 h-3 w-28 rounded-md" />
          <SkeletonPulse className="h-2.5 w-full max-w-md rounded-md" />
        </div>
        <div className="min-h-0 flex-1 [scrollbar-width:thin] overflow-y-auto px-3 py-3">
          <div className="columns-1 [column-gap:0.75rem]">
            {[1, 2].map((i) => (
              <div key={i} className="mb-4 break-inside-avoid">
                <SkeletonPulse className="mb-2 h-2.5 w-3/4 rounded" />
                <SkeletonPulse className="h-[min(220px,28vh)] w-full rounded-xl" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const MetaAdsEditAdPage = () => {
  const { adId } = useParams();
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const apiConfigured = isMetaAdsApiConfigured();
  const unlocked = isMetaAdsExperienceAvailable(apiConfigured);
  const normalizedAdId = String(adId ?? "").trim();

  const {
    data: adDetail,
    isLoading,
    isError,
    error,
  } = useMetaAdsAdForEdit(normalizedAdId, {
    enabled: Boolean(unlocked && normalizedAdId),
  });

  const [name, setName] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [primaryText, setPrimaryText] = useState("");
  const [pageId, setPageId] = useState("");
  const [showPageId, setShowPageId] = useState(false);
  const [campaignBusinessId, setCampaignBusinessId] = useState("");
  const [selectedBusinessRow, setSelectedBusinessRow] = useState(null);
  const [businessAvailability, setBusinessAvailability] = useState({
    total: null,
    loading: true,
  });
  const hydratedKeyRef = useRef(null);

  useEffect(() => {
    if (!adDetail?.adId) {
      return;
    }
    const key = String(adDetail.adId);
    if (hydratedKeyRef.current === key) {
      return;
    }
    hydratedKeyRef.current = key;
    setName(adDetail.name || "");
    setDestinationUrl(adDetail.destinationUrl || "");
    setImageUrl(adDetail.imageUrl || "");
    setPrimaryText(adDetail.primaryText || "");
    setPageId(adDetail.pageId || "");
    const fromUtm = parseMetaAdsStoreIdFromDestinationUrl(
      adDetail.destinationUrl || "",
    );
    const rawMirror =
      adDetail.storeId != null ? String(adDetail.storeId).trim() : "";
    if (fromUtm) {
      setCampaignBusinessId(fromUtm);
    } else if (rawMirror !== "") {
      setCampaignBusinessId(rawMirror);
    }
  }, [adDetail]);

  const handleBusinessesMeta = useCallback((meta) => {
    setBusinessAvailability((prev) => {
      const next = {
        total: meta.total ?? 0,
        loading: Boolean(meta.isLoading),
      };
      if (prev.total === next.total && prev.loading === next.loading) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleSelectBusiness = useCallback((business) => {
    if (business == null) {
      setCampaignBusinessId("");
      setSelectedBusinessRow(null);
      return;
    }
    setCampaignBusinessId(String(business.id));
    setSelectedBusinessRow(business);
  }, []);

  const selectedBusiness = useMemo(() => {
    if (campaignBusinessId === "") {
      return null;
    }
    if (
      selectedBusinessRow != null &&
      String(selectedBusinessRow.id) === String(campaignBusinessId)
    ) {
      return selectedBusinessRow;
    }
    return { id: campaignBusinessId, name: "", email: null, logo: null };
  }, [campaignBusinessId, selectedBusinessRow]);

  const resolvedStoreIdForPrefetch = useMemo(() => {
    const fromLiveUrl = parseMetaAdsStoreIdFromDestinationUrl(destinationUrl);
    if (fromLiveUrl) {
      return fromLiveUrl;
    }
    const storeId = adDetail?.storeId;
    if (storeId == null || storeId === "") {
      return null;
    }
    const trimmed = String(storeId).trim();
    return trimmed !== "" ? trimmed : null;
  }, [destinationUrl, adDetail?.storeId]);

  const canonicalStoreIdForUtm = useMemo(() => {
    const picker = campaignBusinessId.trim();
    if (picker !== "") {
      return picker;
    }
    const fromUrl = parseMetaAdsStoreIdFromDestinationUrl(destinationUrl);
    if (fromUrl) {
      return fromUrl;
    }
    const mirror =
      adDetail?.storeId != null ? String(adDetail.storeId).trim() : "";
    return mirror;
  }, [campaignBusinessId, destinationUrl, adDetail?.storeId]);

  const { data: editPrefetchBusiness } = useQuery({
    queryKey: ["meta-ads-edit-ad-business-by-id", resolvedStoreIdForPrefetch],
    queryFn: async () => {
      const id = Number(resolvedStoreIdForPrefetch);
      if (!Number.isFinite(id)) {
        return null;
      }
      const whereClause = {
        conditions: [{ attribute: "id", value: id }],
        conector: "AND",
      };
      const queryString = buildQueryParams({
        params: "id,logo,name,enabled,email",
        where: whereClause,
        page: 1,
        page_size: 1,
      });
      const response = await apiClient.get(
        `/backoffice/businesses${queryString ? `?${queryString}` : ""}`,
      );
      const list = response?.result ?? [];
      return Array.isArray(list) && list[0] ? list[0] : null;
    },
    enabled:
      Boolean(unlocked && resolvedStoreIdForPrefetch) &&
      isEndpointEnabled("business", "list"),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!editPrefetchBusiness || !resolvedStoreIdForPrefetch) {
      return;
    }
    if (String(campaignBusinessId) !== String(resolvedStoreIdForPrefetch)) {
      return;
    }
    setSelectedBusinessRow(editPrefetchBusiness);
  }, [campaignBusinessId, editPrefetchBusiness, resolvedStoreIdForPrefetch]);

  const destinationUrlNeedsStoreUtmFix = Boolean(
    adDetail &&
    canonicalStoreIdForUtm &&
    !metaAdsDestinationUrlHasCorrectStoreUtm(
      destinationUrl,
      canonicalStoreIdForUtm,
    ),
  );

  const hasAdChanges = Boolean(
    adDetail &&
    (name.trim() !== String(adDetail.name ?? "").trim() ||
      destinationUrl.trim() !== String(adDetail.destinationUrl ?? "").trim() ||
      imageUrl.trim() !== String(adDetail.imageUrl ?? "").trim() ||
      primaryText.trim() !== String(adDetail.primaryText ?? "").trim() ||
      destinationUrlNeedsStoreUtmFix),
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        path: "/growth/meta-ads",
        icon: "Share2",
      },
      {
        label: t("create.editAdBreadcrumbLabel"),
        active: true,
        disabled: true,
      },
    ],
    [t, tNav],
  );

  const saveMutation = useMutation({
    mutationFn: (body) => patchMetaAdsAd(normalizedAdId, body),
    onSuccess: (data) => {
      showToast(t("create.editAdSuccessToast"), "success");
      queryClient.setQueryData(["meta-ads-ad-detail", normalizedAdId], data);
      queryClient.invalidateQueries({
        queryKey: ["meta-ads-graph-campaigns"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["meta-ads-ad-preview", normalizedAdId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["meta-ads-ad-preview-live", normalizedAdId],
        exact: false,
      });
    },
    onError: (err) => {
      showToast(err?.message || t("create.editAdSaveErrorToast"), "error");
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!normalizedAdId || !name.trim()) {
      showToast(t("create.adNameRequired"), "error");
      return;
    }
    if (!destinationUrl.trim()) {
      showToast(t("create.destinationUrlRequired"), "error");
      return;
    }
    if (!imageUrl.trim()) {
      showToast(t("create.imageRequired"), "error");
      return;
    }
    if (!primaryText.trim()) {
      showToast(t("create.primaryTextRequired"), "error");
      return;
    }
    if (!pageId.trim()) {
      showToast(t("create.pageIdRequired"), "error");
      return;
    }
    const canonical = canonicalStoreIdForUtm.trim();
    if (!canonical) {
      showToast(t("create.editAdStoreContextRequiredToast"), "error");
      return;
    }
    let finalDestination = destinationUrl.trim();
    try {
      finalDestination = ensureMetaAdsDestinationUrlStoreUtm(
        finalDestination,
        canonical,
      );
    } catch {
      showToast(t("create.destinationUrlMalformedToast"), "error");
      return;
    }
    if (finalDestination !== destinationUrl.trim()) {
      setDestinationUrl(finalDestination);
    }
    saveMutation.mutate({
      name: name.trim(),
      destinationUrl: finalDestination,
      imageUrl: imageUrl.trim(),
      primaryText: primaryText.trim(),
      pageId: pageId.trim(),
    });
  };

  const disableSubmit =
    !unlocked ||
    isLoading ||
    isError ||
    saveMutation.isPending ||
    !name.trim() ||
    !destinationUrl.trim() ||
    !imageUrl.trim() ||
    !primaryText.trim() ||
    !pageId.trim() ||
    !hasAdChanges;

  return (
    <div className="space-y-10 pb-40 sm:pb-0">
      <Breadcrumb items={breadcrumbItems} />

      {!unlocked && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {t("hub.apiNotConfigured")}
        </div>
      )}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            <span className={metaAdsTitleGradientClass}>
              {t("create.editAdTitleGradientWord")}
            </span>
            <span className="text-foreground">
              {t("create.editAdTitleRest")}
            </span>
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
            {t("create.editAdSubtitle")}
          </p>
        </div>
        <Link
          to="/growth/meta-ads/performance"
          className={cn(
            metaAdsOutlineCtaClass,
            "focus-visible:ring-brand-cyan/35 inline-flex w-full shrink-0 items-center justify-center px-4 py-2.5 focus-visible:ring-2 focus-visible:outline-none sm:w-auto",
          )}
        >
          {t("create.cancelToPerformance")}
        </Link>
      </div>

      {isLoading ? (
        <MetaAdsEditAdPageSkeleton
          ariaLabel={t("create.editAdPageLoadingAria")}
        />
      ) : isError ? (
        <div className="border-destructive/35 bg-destructive/10 rounded-xl border px-4 py-6 text-sm text-red-100">
          {error?.message || t("create.editAdError")}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="grid min-h-0 gap-8 lg:grid-cols-2 lg:items-start">
            <div className="space-y-6">
              <section className={metaAdsFormSectionClass}>
                <div className="mb-5 flex items-center gap-2">
                  <Icon
                    name="CheckCircle2"
                    className="h-5 w-5 text-emerald-400"
                    aria-hidden
                  />
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.adDetailsSection")}
                  </h2>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="meta-ad-name"
                    className={metaAdsFormFieldLabelClass}
                  >
                    {t("create.adName")}
                  </Label>
                  <Input
                    id="meta-ad-name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t("create.adNamePlaceholder")}
                    required
                    variant="dark"
                    size="lg"
                  />
                </div>
              </section>

              {(businessAvailability.loading ||
                (businessAvailability.total ?? 0) > 0 ||
                campaignBusinessId) && (
                <section className={metaAdsFormSectionClass}>
                  <h2 className={metaAdsFormSectionLabelClass}>
                    {t("create.sectionAccount")}
                  </h2>
                  <div className="mt-4">
                    <MetaAdsBusinessPicker
                      className="mt-2"
                      value={campaignBusinessId}
                      onSelect={handleSelectBusiness}
                      onMetaChange={handleBusinessesMeta}
                      pinnedRow={selectedBusinessRow}
                    />
                  </div>
                </section>
              )}

              <section className={metaAdsFormSectionClass}>
                <h2 className={metaAdsFormSectionLabelClass}>
                  {t("create.sectionLanding")}
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t("create.sectionLandingHint")}
                </p>
                <div className="mt-4 space-y-2">
                  <Label
                    htmlFor="meta-ad-destination"
                    className={metaAdsFormFieldLabelClass}
                  >
                    {t("create.destinationUrl")}
                  </Label>
                  <Input
                    id="meta-ad-destination"
                    type="url"
                    value={destinationUrl}
                    onChange={(event) => setDestinationUrl(event.target.value)}
                    placeholder={t("create.destinationUrlPlaceholder")}
                    required
                    variant="dark"
                    size="lg"
                  />
                </div>
              </section>

              <section className={metaAdsFormSectionClass}>
                <h2 className={metaAdsFormSectionLabelClass}>
                  {t("create.sectionFacebookPage")}
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t("create.pageIdReadOnlyHint")}
                </p>
                <div className="mt-4 space-y-2">
                  {pageId ? (
                    <div
                      className={cn(
                        "space-y-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.07] p-3",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <p className="min-w-0 flex-1 font-mono text-sm break-all text-white/90">
                          {showPageId
                            ? pageId
                            : maskNumericIdForDisplay(pageId)}
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowPageId((value) => !value)}
                          className={cn(
                            "text-muted-foreground hover:text-foreground flex shrink-0 items-center justify-center rounded-lg p-1.5 transition",
                            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                          )}
                          aria-pressed={showPageId}
                          aria-label={
                            showPageId
                              ? t("create.pageIdHideToggleAria")
                              : t("create.pageIdShowToggleAria")
                          }
                        >
                          <Icon
                            name={showPageId ? "EyeOff" : "Eye"}
                            className="h-4 w-4"
                            aria-hidden
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                      <div className="flex items-start gap-2">
                        <Icon
                          name="Info"
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-200"
                          aria-hidden
                        />
                        <div className="min-w-0 space-y-1">
                          <p className="text-sm font-medium text-amber-100">
                            {t("create.pageIdNotConfiguredTitle")}
                          </p>
                          <p className="text-xs leading-relaxed text-amber-100/80">
                            {t("create.pageIdNotConfiguredBody")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <section className={metaAdsFormSectionClass}>
                <h2 className={metaAdsFormSectionLabelClass}>
                  {t("create.sectionCreative")}
                </h2>
                <div className="mt-4 space-y-2">
                  <Label
                    htmlFor="meta-ad-image-url"
                    className={metaAdsFormFieldLabelClass}
                  >
                    {t("create.imageUrlLabel")}
                  </Label>
                  <Input
                    id="meta-ad-image-url"
                    type="url"
                    value={imageUrl}
                    onChange={(event) => setImageUrl(event.target.value)}
                    placeholder={t("create.imageUrlPlaceholder")}
                    variant="dark"
                    size="lg"
                  />
                  <p className="text-muted-foreground text-xs">
                    {t("create.imageUrlFieldHint")}
                  </p>
                </div>
                <div className="mt-4 space-y-2">
                  <Label
                    htmlFor="meta-ad-text"
                    className={metaAdsFormFieldLabelClass}
                  >
                    {t("create.primaryText")}
                  </Label>
                  <textarea
                    id="meta-ad-text"
                    value={primaryText}
                    onChange={(event) => setPrimaryText(event.target.value)}
                    rows={4}
                    placeholder={t("create.primaryTextPlaceholder")}
                    className={metaAdsTextareaClass}
                  />
                  <p className="text-muted-foreground text-xs">
                    {t("create.primaryTextHint")}
                  </p>
                </div>
              </section>

              <div className="lg:hidden">
                <MetaAdsCreativePreview
                  metaAdPreviewAdId={normalizedAdId}
                  pageName={selectedBusiness?.name || adDetail?.campaignName}
                  accountLabel={adDetail?.adSetName}
                  primaryText={primaryText}
                  destinationLabel={destinationUrl}
                  imageSrc={imageUrl.trim()}
                  businessLogo={selectedBusiness?.logo}
                  graphLiveDraft={{
                    destinationUrl,
                    imageUrl,
                    primaryText,
                    pageId,
                    campaignObjective: adDetail?.campaignObjective ?? "",
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={disableSubmit}
                className={cn(
                  "flex w-full items-center justify-center gap-2 py-3.5 transition disabled:cursor-not-allowed disabled:opacity-50",
                  metaAdsGradientCtaClass,
                )}
              >
                {saveMutation.isPending ? (
                  t("create.editSubmitting")
                ) : (
                  <>
                    <Icon name="Save" className="h-5 w-5" aria-hidden />
                    {t("create.editSubmitLabel")}
                  </>
                )}
              </button>
            </div>

            <div className="hidden min-h-0 lg:sticky lg:top-6 lg:z-10 lg:flex lg:h-[calc(100svh-1.75rem)] lg:max-h-[calc(100svh-1.75rem)] lg:min-h-0 lg:flex-col lg:self-start lg:overflow-hidden">
              <MetaAdsCreativePreview
                metaAdPreviewAdId={normalizedAdId}
                pageName={selectedBusiness?.name || adDetail?.campaignName}
                accountLabel={adDetail?.adSetName}
                primaryText={primaryText}
                destinationLabel={destinationUrl}
                imageSrc={imageUrl.trim()}
                businessLogo={selectedBusiness?.logo}
                graphLiveDraft={{
                  destinationUrl,
                  imageUrl,
                  primaryText,
                  pageId,
                  campaignObjective: adDetail?.campaignObjective ?? "",
                }}
                fullViewportColumn
                className="min-h-0 flex-1"
              />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default MetaAdsEditAdPage;
